import {
    IsBoolean,
    IsEnum,
    IsNotEmpty,
    IsNumber,
    IsOptional,
    IsString,
    ValidateIf,
    ValidateNested
} from 'class-validator'
import type { Address } from '../../utils'
import type { Guardian } from '../../students'

export enum AddressVerificationMethod {
    API = 'api',
    LETTER = 'letter',
    NO_CHECK = 'noCheck'
}

export enum AddressVerificationApiVersion {
    MESO_43 = 'Meso_43',
    XMELD_30 = 'XMeld_30'
}

export class Proxy {
    @IsString()
    @IsNotEmpty()
    host: string

    @IsNumber()
    port: number
}

export class AddressVerificationConfiguration {
    @IsEnum(AddressVerificationMethod)
    method: AddressVerificationMethod

    @IsEnum(AddressVerificationApiVersion)
    @ValidateIf((context) => context.method === AddressVerificationMethod.API)
    apiSpec?: AddressVerificationApiVersion

    @ValidateIf((context) => context.apiSpec)
    @IsBoolean()
    runVerificationCron?: boolean

    @ValidateIf((context) => context.apiSpec)
    @IsString()
    @IsNotEmpty()
    apiUrl?: string

    @IsOptional()
    @ValidateNested()
    proxy: Proxy

    @IsOptional()
    @IsBoolean()
    useCert?: boolean

    // Not set on DB but as environment variable `EWO_CERT_<tenant_key>`!!!
    @ValidateIf((context) => context.useCert)
    @IsString()
    @IsNotEmpty()
    sslCert?: string

    // Not set on DB but as environment variable `EWO_KEY_<tenant_key>`!!!
    @ValidateIf((context) => context.useCert)
    @IsString()
    @IsNotEmpty()
    sslKey?: string
}

export class MesoApiConfiguration extends AddressVerificationConfiguration {
    @IsNumber()
    cityMandant: number
}

export class XMeldApiConfiguration extends AddressVerificationConfiguration {
    @IsString()
    @IsNotEmpty()
    customerName: string

    @ValidateNested()
    customerAddress: Address

    @IsString()
    @IsNotEmpty()
    customerEmail: string

    @IsString()
    @IsNotEmpty()
    customerPhone: string

    @IsString()
    @IsNotEmpty()
    providerName: string

    // See XMeldQueryType: either 'personensuche' or 'freiesuche'
    // If set to 'personensuche', only queries for the AGS of the customer are possible.
    // If set to 'freiesuche', queries in the entire state of the customer are possible. This requires the AGS of the federal state (see below).
    // If not set, falls back to 'personensuche'
    @IsOptional()
    @IsString()
    queryType?: string

    // The AGS of the municipality where the customer is located.
    // If queryType is 'personensuche', this is used for both author and reader of the query, and the municpipality to be queried.
    // If queryType is 'freiesuche', this is used only for the author of the query.
    @IsString()
    @IsNotEmpty()
    municipalityKey: string

    // Required if queryType is 'freiesuche'.
    // This is the key of the federal state of the customer and is used for the reader of the query.
    @ValidateIf((context) => context.queryType === 'freiesuche')
    @IsString()
    @IsNotEmpty()
    readerMunicipalityKey?: string

    // Required if queryType is 'freiesuche'.
    // See urn:de:bund:destatis:bevoelkerungsstatistik:schluessel:bundesland
    @ValidateIf((context) => context.queryType === 'freiesuche')
    @IsString()
    @IsNotEmpty()
    federalStateKey?: string

    // Wrapper around XMeld payload - {xmeld_payload} will be replaced with the payload (Usually does not change).
    @IsString()
    @IsNotEmpty()
    soapEnvelope: string

    // Whether to base64 encode the XMeld payload (normally: true).
    @IsBoolean()
    encodePayload: boolean = false

    // Location of XMeld result in response body (normally: S:Body.response.xmeld.result).
    @IsString()
    @IsNotEmpty()
    responseKey: string
}

export type AddressVerificationApiParams<ApiConfig extends AddressVerificationConfiguration> = {
    guardian: Guardian
    address: Address
    configuration: ApiConfig
}
